import { useState } from 'react';

import { connect } from 'react-redux';

import { shortenText } from '@/helpers/string';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useTranslation } from '@/i18n';
import { ServerStatus, SideBarUserInfoPropsType } from '@/models/globalModels';
import { doSaveAmbassador } from '@/redux/auth/actions';
import { selectSideBarProfileData } from '@/redux/auth/reducers';
import { RootState } from '@/redux/rootReducer';

import EditAvatarModal from './EditAvatarModal/EditAvatarModal';
import Gate from './Gate';
import { InlineImageUpload } from './InlineImageEditor';
import { Loader } from './Loader';
import { useModals } from './modal/ModalManagerProvider';

const mapStateToProps = (state: RootState) => ({
  userInfo: selectSideBarProfileData(state),
  uid: state.auth.ambassadorProfile?.uid,
  saveAmbassadorStatus: state.auth.saveAmbassadorStatus,
});

const mapDispatchToProps = {
  doSaveAmbassador,
};

const obfuscateEmail = (email?: string) => {
  if (!email) return '';
  const [user, domain = ''] = email.split('@');
  if (!domain) return email;

  const mask = (s: string, keep = 2) => (s.length <= keep ? (s[0] ?? '') + '***' : s.slice(0, keep) + '***' + s.slice(-1));

  const [host, ...rest] = domain.split('.');
  const maskedUser = mask(user, 2);
  const maskedHost = mask(host, 1);
  return `${maskedUser}@${maskedHost}${rest.length ? '.' + rest.join('.') : ''}`;
};

const SideBarUserInfo = ({ userInfo, doSaveAmbassador, uid, saveAmbassadorStatus }: SideBarUserInfoPropsType) => {
  const { t } = useTranslation('dashboard');
  const defaultProfileImg = '/images/generic_avatar.png';
  const isLoading = saveAmbassadorStatus === ServerStatus.FETCHING;
  const fullName = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim();

  // storefront conditonal caps/flags
  const caps = useCapabilities?.();
  const featureFlags = (caps?.featureFlags as Set<string>) ?? new Set<string>();

  const hasFlag = (k: string) => featureFlags.has(k);
  const shouldObfuscateEmail = hasFlag('obfuscate_email');

  const { openModal } = useModals();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderedEmail = shouldObfuscateEmail ? obfuscateEmail(userInfo?.email) : userInfo?.email;

  return (
    <div className="align-items-center justify-content-center">
      <div className="d-flex justify-content-center align-items-center mt-3">
        {isLoading ? (
          <div className="userAvatarLoader h-100 d-flex align-items-center">
            <Loader size={30} strokeSize={4} isRelative />
          </div>
        ) : (
          <Gate allow="sidebar_profile_image">
            <div className="d-flex justify-content-center align-items-center mt-3">
              <InlineImageUpload
                className="userAvatar w-100"
                onUpload={(data: File) => doSaveAmbassador(uid, { avatar_url: data }, true)}
                preview={userInfo?.avatar_url || defaultProfileImg}
                readonly={true}
                onClick={() => {
                  if (!isModalOpen) {
                    openModal(EditAvatarModal, {
                      title: t('common:imageUpload.editImage'),
                      size: 'sm',
                      existingImageUrl: userInfo?.avatar_url,
                      onSave: (file: File) => doSaveAmbassador(uid, { avatar_url: file }, true),
                      onOpen: () => setIsModalOpen(true),
                      onClose: () => setIsModalOpen(false),
                    });
                  }
                }}
              />
            </div>
          </Gate>
        )}
      </div>

      <div className="userInfoWrapper mt-3 f-raleway-regular">
        <ul className="list-group list-unstyled f-14 f-raleway-regular text-center">
          <Gate allow="sidebar_full_name">
            <li className="f-14 fw-600 userFullName text-center mb-2">{shortenText(fullName, 28)}</li>
          </Gate>

          <li className="f-12 fw-500 userEmail mb-3">{shortenText(renderedEmail || '', 25)}</li>

          <Gate allow="sidebar_joined_date">
            <li className="f-12 fw-500 userJoinDate mb-3">
              {t('sidebar.joined')}: {userInfo?.created_at}
            </li>
          </Gate>
        </ul>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideBarUserInfo);
